// Store global hrefs here, rather than inlining them in the code.

import { isParadiseProdEnv, isProdEnv } from "src/constants/env"
import { getStoredAuthorization } from "src/data/auth/auth"
import { TParadiseDevice } from "src/data/paradise/paradiseDevices/types/paradiseDeviceQueryTypes"

export const HREF_MINUT_STORE = "https://store.minut.com"

export const HREF_MINUT_STAGING_STORE = "https://store.staging.minut.com"

export const HREF_MINUT_BILLING = "/billing"

export const HREF_MINUT_HELP = "https://support.minut.com"

export const HREF_MINUT_TERMS_OF_SERVICE =
  "https://www.minut.com/service/terms-of-service/"

export const HREF_MINUT_PRIVACY_POLICY = `https://www.minut.com/service/privacy-policy/`

export const HREF_MINUT_HELP_CROWD_DETECT =
  "https://support.minut.com/en/articles/5020397-crowd-detect"

export const HREF_MINUT_HELP_HOME_PROFILES =
  "https://support.minut.com/en/articles/5172900-profiles"

export const HREF_MINUT_HELP_NOISE_PRESETS =
  "https://support.minut.com/en/articles/10086187-noise-presets"

export const HREF_MINUT_HELP_SHORTCODES =
  "https://support.minut.com/en/articles/5356196-guest-connect-check-in-check-out-message"

export const HREF_MINUT_MRS_BILLING_REGIONS =
  "https://support.minut.com/en/articles/5967200-currently-available-locations-and-billing"

export const HREF_MINUT_HELP_LEGACY_PLANS =
  "https://support.minut.com/en/articles/6167256-legacy-plans-and-changes-to-current-plans"

export const HREF_MINUT_HOME_GROUPS =
  "https://support.minut.com/en/articles/6457273-organizations-home-groups"

export const HREF_MINUT_ORGANIZATIONS_MEMBER_INVITE =
  "https://support.minut.com/en/articles/6457223-organizations-how-to-invite-members"

export const HREF_MINUT_ORGANIZATION_PERMISSIONS =
  "https://support.minut.com/en/articles/6515398-organizations-members-roles-access-level"

export const HREF_MINUT_HELP_SENSORS_OFFLINE =
  "https://support.minut.com/en/articles/7872966-what-to-do-when-your-sensor-turns-offline"

export const HREF_MINUT_HELP_SENSOR_SWAP =
  "https://support.minut.com/en/articles/7964094-sensor-swap"

export const HREF_MINUT_GUARD_ASSIST =
  "https://support.minut.com/en/collections/3322493-guard-assist-previously-minut-response-partners"

export const HREF_MINUT_HELP_CALL_ASSIST =
  "https://support.minut.com/en/articles/8376790-what-is-call-assist"

export const HREF_MINUT_REGISTER_GUARD_ASSIST =
  "https://minut-hq.typeform.com/to/xKdeHG3E"

export const HREF_MINUT_MARKETING_CALL_ASSIST_INFO =
  "https://minut.com/product/call-assist"

export const HREF_MINUT_INTEGRATION_LET_US_KNOW =
  "https://minut-hq.typeform.com/to/LK1Hobs9?typeform-source=www.minut.com"

export const HREF_MINUT_SUBSCRIPTION_TERMS =
  "https://www.minut.com/legal/subscription-terms"

export const HREF_CIGARETTE_FALSE_NEGATIVE_REPORT_FORM =
  "https://forms.gle/r2Nk5SmAweMrfDqq6"

export const HREF_MINUT_HELP_INDOOR_CLIMATE_MONITORING =
  "http://support.minut.com/en/articles/9208390-indoor-climate-monitoring"

export const HREF_MINUT_HELP_DESKTOP_NOTIFICATIONS =
  "https://support.minut.com/en/articles/9361239-desktop-notifications"

export const HREF_MINUT_CONNECT_WITH_AIRBNB =
  "https://support.minut.com/en/articles/6267173-connect-with-airbnb"

export const HREF_MINUT_REFERRAL_PROGRAM_TC =
  "https://www.minut.com/legal/refer-a-friend-program-terms-conditions"

export const HREF_MINUT_REFERRAL_FAQ =
  "https://www.minut.com/resources/refer-a-friend#faq"

export const HREF_BUILDING_HOMES_CSV_TEMPLATE =
  "https://assets.minut.com/templates/building-homes.csv"

export const HREF_MINUT_HELP_DECREASE_MOLD_RISK =
  "https://support.minut.com/en/articles/10365410-how-to-decrease-mold-risk-in-your-property"

export const HREF_MINUT_HELP_MOLD_RISK =
  "https://support.minut.com/en/articles/8597251-mold-risk"

export const HREF_MINUT_SMOKING_DETECTION =
  "https://support.minut.com/en/articles/7964407-smoking-detection"

export const HREF_MINUT_TASKS_FEEDBACK_FORM =
  "https://forms.gle/YuNvWRjHVF1odMnP9"

export const HREF_MINUT_PORTFOLIO_ANALYTICS_BLOG_POST =
  "https://www.minut.com/blog/minut-portfolio-analytics-dashboard"

function accountManageHref({
  accessToken,
  path,
  redirectToWeb,
}: {
  accessToken?: string
  path: string
  redirectToWeb?: boolean
}) {
  const url = new URL(path, import.meta.env.REACT_APP_API_URL)

  if (accessToken) {
    url.searchParams.set("session_from_token", accessToken)
  }

  if (redirectToWeb) {
    url.searchParams.set("redirectToWeb", "true")
  }

  return url.toString()
}

export function changePasswordUrl() {
  return accountManageHref({
    accessToken: getStoredAuthorization()?.access_token,
    path: "change_password",
    redirectToWeb: true,
  })
}

export function resetPasswordUrl() {
  return accountManageHref({ path: "account/password" })
}

/**
 * Function that builds a kibana url for a given user and orgs.
 * Since alot of newer be-core logs don't include the orgId, including
 * the orgId in the query params will likely find more logs.
 * This builds a "Rison" global and app state that kibana can interpret.
 */
export function kibanaUrl(userId: string) {
  const timeBefore = "48h"

  const baseUrl = isProdEnv
    ? "https://kibana.minut.com"
    : "https://kibana.staging.minut.com"

  const index = isProdEnv
    ? "5735e5c0-383b-11ee-9faf-55a24f934342"
    : "5af1e1c0-1d63-11e9-b21c-6376c802ffd9"

  // This is the base path to get to the log viewer
  let url = `${baseUrl}/_dashboards/app/discover#/`

  // Add the "global" state _g=()
  url += `?_g=(time:(from:now-${timeBefore},to:now))`

  // Build the Rison query that filters for either the user or any of the orgs provided
  const rison = `(columns:!(msg),filters:!(('$state':(store:appState),meta:(index:'${index}',key:user_id,negate:!f,params:(query:'${userId}'),type:phrase),query:(match_phrase:(user_id:'${userId}')))),index:'${index}',query:(language:lucene,query:''),sort:!())`

  // Add the "app" state _a=()
  url += `&_a=${rison}`

  return url
}

function chargebeeBaseUrl() {
  return isParadiseProdEnv
    ? "https://minut.chargebee.com"
    : "https://minut-test.chargebee.com"
}

export function chargebeeUrl(userId: string) {
  return `${chargebeeBaseUrl()}/admin-console/customers/${userId}`
}

export function chargebeeInvoiceUrl(invoiceId: string) {
  return `${chargebeeBaseUrl()}/d/invoices/${invoiceId}`
}

export function chargebeeOrderUrl(orderId: string) {
  return `${chargebeeBaseUrl()}/d/orders/${orderId}`
}

export function chargebeeAddonUrl(addonId: string) {
  return `${chargebeeBaseUrl()}/admin-console/addons/${addonId}`
}

export function chargebeeSubscriptionUrl(subscriptionId: string) {
  return `${chargebeeBaseUrl()}/d/subscriptions/${subscriptionId}`
}

export function intercomUrl(email: string) {
  return `https://app.intercom.io/a/apps/noqdedef/inbox/inbox/search?q=${email}`
}

export function kibanaDeviceUrl(device: TParadiseDevice) {
  return `https://kibana.minut.com/_plugin/kibana/app/kibana#/discover?_g=(refreshInterval:(display:Off,pause:!f,value:0),time:(from:now-2h,mode:quick,to:now))&_a=(columns:!(module,msg),index:'point-*',interval:auto,query:(query_string:(analyze_wildcard:!t,query:'device:${
    device.hardware_version < 100
      ? device.device_mac.substring(6, 12)
      : device.device_mac
  }')),sort:!('@timestamp',desc))`
}

export function firmwareCrashReportUrl(deviceMac: string) {
  return `https://fwcrs.minut.com/dash/reports?mac=${deviceMac}`
}

export const paradiseUrl = {
  production: "https://paradise2.minut.com",
  staging: "https://paradise2.staging.minut.com",
}

export const webUrl = {
  production: "https://web.minut.com",
  staging: "https://web.staging.minut.com",
}

export const storeUrl = {
  production: "https://store.minut.com",
  staging: "https://store.staging.minut.com",
}
